import { TitleRow } from "lego-gui-lib/lib/components/Views/TitleRow";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Route, useHistory, useLocation, useRouteMatch } from "react-router";
import { Link } from "react-router-dom";
import { IconButton, Tooltip } from "@material-ui/core";
import { GoBackButton, RemoteDataSource, withBreadCrumbs } from "lego-gui-lib";
import { DestinationViewComponent } from "../../destination/views";
import { Build } from "@material-ui/icons";
import { TreeViewComponent } from "../../tree/views/views";
import { SanContext } from "../schema";
import { TabContent, TabData } from "../tabs/TabContent";
import { TabHeader } from "../tabs/TabHeader";
import { QueueConfigurationViewComponent } from "../../queueConfig/views";
import { HuntingGroupViewComponent } from "../../huntingGroup/views";
import { serviceProfileAllows } from "../../serviceProfile/validator";
import { observer } from "mobx-react-lite";
import { IvrMenuViewComponent } from "../../ivrMenu/views";
import { CallLogViewComponent } from "../../callLog/views";
import { alwaysTrueFn } from "../../../common/common";
import { ReportSubscriptionViewComponent } from "../../reportSubscription/views";
import { Loader } from "../../../common/loadingComponent";
import get from "lodash/get";

const preparePathBasedTabId = (pathname: string) => {
  const pathnameParts = pathname.split("/");
  const tabIdPathParts = pathnameParts.slice(1, 5);
  return tabIdPathParts.join("/");
};

export const Show = observer(() => {
  const history = useHistory();
  const location = useLocation();

  const SAN = useContext(SanContext);

  const { t } = useTranslation();
  const title = t("hns:san:domain");
  const match = useRouteMatch();
  const baseUrl = match.url;

  const tabs: (SAN: RemoteDataSource<any, any>) => TabData[] = SAN => {
    return [
      {
        label: t("hns:destination:domain_plural"),
        path: "/destinations",
        Element: <DestinationViewComponent />
      },
      {
        label: t("hns:ivrMenu:domain_plural"),
        path: "/ivrMenus",
        Element: <IvrMenuViewComponent />
      },
      {
        label: t("hns:huntingGroup:domain_plural"),
        path: "/huntingGroups",
        Element: <HuntingGroupViewComponent />,
        if: () =>
          serviceProfileAllows(
            { data: SAN.oneDataSource.one },
            "featCallHunting"
          )
      },
      {
        label: t("hns:queueConfig:domain_plural"),
        path: "/queueConfigurations",
        Element: <QueueConfigurationViewComponent />,
        if: () =>
          serviceProfileAllows({ data: SAN.oneDataSource.one }, "featQueue")
      },
      {
        label: t("hns:tree:domain_plural"),
        path: "/trees",
        Element: <TreeViewComponent />,
        if: () =>
          serviceProfileAllows(
            { data: SAN.oneDataSource.one },
            "routingTypeTree"
          )
      },
      {
        label: t("hns:callLog:domain_plural"),
        path: "/callLogs",
        Element: <CallLogViewComponent />,
        if: () =>
          serviceProfileAllows({ data: SAN.oneDataSource.one }, "featCallLog")
      },
      {
        label: t("hns:reportSubscription:domain_plural"),
        path: "/reportSubscriptions",
        Element: <ReportSubscriptionViewComponent />,
        if: () =>
          serviceProfileAllows({ data: SAN.oneDataSource.one }, "featReporting")
      }
    ];
  };

  const goBack = () => {
    history.goBack();
  };

  const filteredTabs = React.useMemo(
    () =>
      tabs(SAN).filter(tab => {
        const iff = tab.if || alwaysTrueFn;
        return iff();
      }),
    [tabs, SAN.oneDataSource.one]
  );

  if (!SAN.oneDataSource.one) {
    return <Loader />;
  }

  const sanMainNumber = get(SAN, "oneDataSource.one.mainNumber");

  return withBreadCrumbs(
    { to: baseUrl + "/edit", children: title + " (" + sanMainNumber + ")" },
    <>
      <Route
        exact={true}
        path={baseUrl + "/:type"}
        render={() => {
          return (
            <>
              <TitleRow
                classes={{ titleText: "", title: "" }}
                title={title + " (" + sanMainNumber + ")"}
                prefix={<GoBackButton goBack={goBack} />}
                suffix={
                  <Tooltip title={t("common:config") as string}>
                    <Link to={baseUrl + "/configure"}>
                      <IconButton color="primary">
                        <Build />
                      </IconButton>
                    </Link>
                  </Tooltip>
                }
              />
              <TabHeader
                baseUrl={baseUrl}
                tabs={filteredTabs}
                value={preparePathBasedTabId(location.pathname)}
                identifyTab={preparePathBasedTabId}
              />
            </>
          );
        }}
      />
      <TabContent baseUrl={baseUrl} tabs={filteredTabs} />
    </>
  );
});
